import { Link } from "@tanstack/react-router";
import { ArrowUpRight, Check, MapPin } from "lucide-react";
import { Section } from "@/components/Section";
import { Reveal } from "@/components/Reveal";
import type { Location } from "@/content/locations";
import type { Service } from "@/content/services";
import { isPairedCity } from "@/content/language-pairs";
import { FaqCardGrid } from "@/components/FaqCardGrid";

/** Sections of a city × service page. */

export function CityServiceHero({
  loc,
  svc,
  angle,
}: {
  loc: Location;
  svc: Service;
  angle: string;
}) {
  return (
    <>
      <h1 className="sr-only">
        {svc.title} in {loc.city}, {loc.country}
      </h1>

      <Section
        eyebrow={`${loc.city} · ${svc.title}`}
        title={
          <>
            {svc.title} in <span className="text-gradient">{loc.city}.</span>
          </>
        }
      >
        <div className="grid lg:grid-cols-3 gap-10 -mt-4">
          <Reveal className="lg:col-span-2">
            <p className="text-xl text-muted-foreground leading-relaxed">{angle}</p>
            <p className="mt-6 text-muted-foreground leading-relaxed">{svc.description}</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-6 py-3 text-sm font-medium hover:opacity-90"
              >
                Scope this for {loc.city} <ArrowUpRight size={16} />
              </Link>
              <Link
                to="/free-seo-audit"
                className="inline-flex items-center gap-2 rounded-full border border-border px-6 py-3 text-sm font-medium hover:bg-card"
              >
                Free SEO audit
              </Link>
              <Link
                to="/seo-services/$location"
                params={{ location: loc.slug }}
                className="inline-flex items-center gap-2 rounded-full border border-border px-6 py-3 text-sm font-medium hover:bg-card"
              >
                All {loc.city} services
              </Link>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="rounded-3xl border border-border p-8 bg-card space-y-4">
              <div className="flex items-center gap-3">
                <MapPin className="text-primary" size={18} />
                <div>
                  <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
                    Service area
                  </div>
                  <div className="font-display text-lg">
                    {loc.city}, {loc.region}
                  </div>
                </div>
              </div>
              <div className="border-t border-border pt-4 text-sm text-muted-foreground leading-relaxed">
                Industries I regularly help in {loc.city}: {loc.industries.slice(0, 4).join(", ")}.
              </div>
            </div>
          </Reveal>
        </div>
      </Section>
    </>
  );
}

export function CityServiceDeliverables({
  loc,
  svc,
  pillars,
}: {
  loc: Location;
  svc: Service;
  pillars: string[];
}) {
  return (
    <Section
      eyebrow="What's included"
      title={
        <>
          Deliverables for <span className="text-gradient">{loc.city} {svc.title}.</span>
        </>
      }
    >
      <div className="grid md:grid-cols-2 gap-6">
        {pillars.map((p, i) => (
          <Reveal key={p} delay={i * 0.04}>
            <div className="flex gap-4 rounded-3xl border border-border p-7 bg-card h-full">
              <span className="mt-1 inline-flex size-8 shrink-0 items-center justify-center rounded-full bg-primary/15 text-primary">
                <Check size={16} />
              </span>
              <p className="text-muted-foreground leading-relaxed">{p}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </Section>
  );
}

export function CityServiceOutcomes({ loc, svc }: { loc: Location; svc: Service }) {
  return (
    <Section
      eyebrow="Goals"
      title={
        <>
          What the work in <span className="text-gradient">{loc.city}</span> aims for.
        </>
      }
    >
      <div className="grid md:grid-cols-3 gap-4">
        {svc.outcomes.map((o, i) => (
          <Reveal key={o} delay={i * 0.04}>
            <div className="rounded-3xl border border-border p-6 bg-card">
              <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
                Goal
              </div>
              <div className="mt-2 font-display text-lg leading-snug">{o}</div>
            </div>
          </Reveal>
        ))}
      </div>
    </Section>
  );
}

export function CityServiceFaq({
  loc,
  svc,
  faqs,
}: {
  loc: Location;
  svc: Service;
  faqs: { q: string; a: string }[];
}) {
  return (
    <Section
      eyebrow={`${svc.title} FAQ`}
      title={
        <>
          Common questions from <span className="text-gradient">{loc.city} buyers.</span>
        </>
      }
    >
      <FaqCardGrid items={faqs} />
    </Section>
  );
}

/** Other services in the same city plus the contextual link row. */
export function CityServiceSiblings({
  loc,
  svc,
  siblings,
}: {
  loc: Location;
  svc: Service;
  siblings: Service[];
}) {
  return (
    <Section
      eyebrow={`Other ${loc.city} services`}
      title={
        <>
          Pair this with <span className="text-gradient">another {loc.city} playbook.</span>
        </>
      }
    >
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {siblings.map((s) => (
          <Link
            key={s.slug}
            to="/seo-services/$location/$service"
            params={{ location: loc.slug, service: s.slug }}
            className="group rounded-3xl border border-border p-6 bg-card hover:border-primary transition"
          >
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
                  {loc.city}
                </div>
                <div className="mt-1 font-display text-lg leading-snug">{s.title}</div>
              </div>
              <ArrowUpRight
                size={18}
                className="text-muted-foreground group-hover:text-primary shrink-0"
              />
            </div>
            <p className="mt-3 text-sm text-muted-foreground leading-relaxed">{s.tagline}</p>
          </Link>
        ))}
      </div>
      <p className="mt-8 text-sm text-muted-foreground">
        More context:{" "}
        <Link
          to="/seo-services/$location"
          params={{ location: loc.slug }}
          className="underline hover:text-foreground"
        >
          {loc.city} SEO hub
        </Link>{" "}
        ·{" "}
        <Link to="/services" className="underline hover:text-foreground">
          {svc.title} nationwide
        </Link>{" "}
        ·{" "}
        <Link to="/seo-locations" className="underline hover:text-foreground">
          All locations
        </Link>{" "}
        ·{" "}
        <Link to="/seo-services" className="underline hover:text-foreground">
          Full city × service index ({loc.city} included)
        </Link>{" "}
        ·{" "}
        <Link to="/case-studies" className="underline hover:text-foreground">
          Case studies
        </Link>
        {isPairedCity(loc.slug) && (
          <>
            {" "}
            ·{" "}
            <Link
              to="/bn/seo-services/$location/$service"
              params={{ location: loc.slug, service: svc.slug }}
              hrefLang="bn"
              className="underline hover:text-foreground"
            >
              {loc.city}-এ {svc.title} — বাংলায় পড়ুন
            </Link>
          </>
        )}
      </p>
    </Section>
  );
}
